import React, {useCallback, useMemo} from "react";
import AppModal from "../../core/components/app-modal";
import {ModalIds} from "@common/models/enums/modal-ids.enum";
import {animated} from "@react-spring/web";
import {useModalAnimation} from "../../core/animations";
import useModalWithData from "@common/hooks/use-modal-with-data";
import AppButton from "@common/components/app-button";
import ArticleTag from "./ui/article-tag";
import {convertLexicalJsonToHtml} from "../helpers/lexical-to-html";
import {addAnchorsToContent, generateTableOfContents} from "../helpers/generate-table-of-contents";
import {sanitizeHTML} from "../helpers/safe-html-renderer";
import {ArticleDetails} from "../../core/models/interfaces/article-details.interface";
import {AiGeneratedArticlePayload} from "../models/interfaces/ai-generated-article.interface";

export interface AiConvertedArticleModalData {
    articleData?: ArticleDetails;
    aiPayload?: AiGeneratedArticlePayload;
    onAccept?: (payload: AiGeneratedArticlePayload) => void;
    acceptLabel?: string;
}

const AiConvertedArticleModal: React.FC = () => {
    const {
        data,
        hideModal,
        isOpenModal
    } = useModalWithData<AiConvertedArticleModalData>(ModalIds.ARTICLE_AI_CONVERTED_PREVIEW);
    const modalOpen = isOpenModal();
    const {springProps, closeModal} = useModalAnimation(!!modalOpen, "medium");
    const article: ArticleDetails | undefined = data?.articleData;

    const handleImageLoading = (container: HTMLElement): void => {
        const images = container.querySelectorAll('img');
        images.forEach((img: HTMLImageElement) => {
            const parentWrapper = img.closest('.image-node') as HTMLElement;

            if (img.complete && img.naturalHeight !== 0) {
                img.classList.remove('loading');
                if (parentWrapper) {
                    parentWrapper.classList.remove('loading');
                }
            } else {
                img.classList.add('loading');
                if (parentWrapper) {
                    parentWrapper.classList.add('loading');
                }

                img.onload = () => {
                    img.classList.remove('loading');
                    if (parentWrapper) {
                        parentWrapper.classList.remove('loading');
                    }
                };

                img.onerror = () => {
                    img.classList.remove('loading');
                    img.classList.add('error');
                    if (parentWrapper) {
                        parentWrapper.classList.remove('loading');
                        parentWrapper.classList.add('error');
                    }
                };
            }
        });
    };

    const tableOfContents = useMemo(() => {
        if (!article?.content) return [];

        let htmlContent = article.content;
        try {
            if (article.content.startsWith('{') || article.content.startsWith('[')) {
                htmlContent = convertLexicalJsonToHtml(article.content);
            }
        } catch (error) {
            console.warn('Error converting Lexical JSON to HTML for TOC:', error);
        }

        return generateTableOfContents(htmlContent);
    }, [article?.content]);

    const safeTitle: string = useMemo(() => {
        if (!article?.title) return '';

        let htmlTitle = article.title;
        try {
            if (article.title.startsWith('{') || article.title.startsWith('[')) {
                htmlTitle = convertLexicalJsonToHtml(article.title);
            }
        } catch (error) {
            console.warn('Error converting title Lexical JSON to HTML:', error);
        }

        return sanitizeHTML(htmlTitle);
    }, [article?.title]);

    const safeSummary: string = useMemo(() => {
        if (!article?.summary) return '';

        let htmlSummary = article.summary;
        try {
            if (article.summary.startsWith('{') || article.summary.startsWith('[')) {
                htmlSummary = convertLexicalJsonToHtml(article.summary);
            }
        } catch (error) {
            console.warn('Error converting summary Lexical JSON to HTML:', error);
        }

        return sanitizeHTML(htmlSummary);
    }, [article?.summary]);

    const safeContent: string = useMemo(() => {
        if (!article?.content) return '';

        let htmlContent = article.content;
        try {
            if (article.content.startsWith('{') || article.content.startsWith('[')) {
                htmlContent = convertLexicalJsonToHtml(article.content);
            }
        } catch (error) {
            console.warn('Error converting Lexical JSON to HTML:', error);
        }

        const contentWithAnchors = addAnchorsToContent(htmlContent);
        return sanitizeHTML(contentWithAnchors);
    }, [article?.content]);

    const handleAccept = useCallback(() => {
        if (data?.aiPayload && data?.onAccept) {
            data.onAccept(data.aiPayload);
        }
        closeModal(() => hideModal());
    }, [data, hideModal]);

    if (!article) return null;

    return (
        <AppModal
            id={ModalIds.ARTICLE_AI_CONVERTED_PREVIEW}
            key={ModalIds.ARTICLE_AI_CONVERTED_PREVIEW}
            zIndex={60}
            forceFixed={true}
            classModal="max-w-4xl flex justify-center">
            <animated.div
                style={springProps}
                className="h-full flex flex-col bg-surface-level-1 rounded-2xl max-h-[85svh] md:max-h-[80vh] min-w-full md:min-w-[640px] max-w-[640px]"
            >
                <div className="flex items-center pl-8 pr-4 py-3 border-b border-outline-mild h-[72px] max-h-[72px]">
                    <h3 className="text-lg font-semibold leading-snug text-transparent bg-clip-text bg-gradient-to-r from-[#1B59B6]/100 via-[#1B59B6]/80 to-[#CB00C3]/70">
                        Artykuł poprawiony przez AI
                    </h3>
                </div>

                <div
                    className="px-8 pt-8 pb-6 flex flex-col gap-6 overflow-y-auto scroll-smooth preview-article relative">
                    <div className="inline-block align-middle text-sm text-on-surface/60 w-fit">
                        {article?.tags?.map((tag, index) => (
                            <React.Fragment key={index}>
                                <ArticleTag text={tag}/>
                                {index < article?.tags.length - 1 && <span className="mx-2">·</span>}
                            </React.Fragment>
                        ))}
                    </div>

                    <div className="flex-1 flex flex-col gap-6 preview">
                        <h1
                            className="!text-black"
                            dangerouslySetInnerHTML={{__html: safeTitle}}/>

                        <p
                            className="!text-[#334155]"
                            dangerouslySetInnerHTML={{__html: safeSummary}}/>

                        {tableOfContents.length > 0 && (
                            <ul className="space-y-2 text-link ml-5">
                                {tableOfContents.map((item, index) => (
                                    <li key={index} className="flex items-center font-semibold text-base">
                                        <div className="w-1 h-1 bg-link rounded-full mr-3 flex-shrink-0"></div>
                                        <a
                                            href={`#${item.id}`}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                const element = document.getElementById(item.id);
                                                if (element) {
                                                    element.scrollIntoView({
                                                        behavior: 'smooth',
                                                        block: 'start',
                                                        inline: 'nearest'
                                                    });
                                                }
                                            }}
                                            className="cursor-pointer hover:text-link-hover transition-colors duration-200"
                                        >
                                            {item.title}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        )}

                        <div
                            className="[&>*]:!text-[#334155]"
                            dangerouslySetInnerHTML={{__html: safeContent}}
                            ref={(el) => {
                                el && handleImageLoading(el)
                            }}
                        />
                    </div>
                </div>

                <div
                    className="flex items-center flex-wrap md:flex-nowrap justify-end gap-2 py-3 md:py-0 md:h-[72px] md:min-h-[72px] rounded-b-2xl border-t border-outline-mild px-8">
                    <AppButton
                        onClick={() => hideModal()}
                        className="cursor-pointer bg-transparent hover:bg-on-surface/5 w-full md:w-fit max-h-[40px] h-[40px] !px-6 !pt-2 border-[2px] !border-on-surface font-semibold text-base !rounded-full"
                        label="Anuluj"
                    />
                    <AppButton
                        className="cursor-pointer hover:bg-primary/80 bg-primary w-full md:w-fit max-h-[40px] h-[40px] !px-6 !pt-2 text-on-primary font-semibold text-base !rounded-full"
                        onClick={handleAccept}
                        disabled={!data?.onAccept || !data?.aiPayload}
                        label={data?.acceptLabel ?? "Zaakceptuj zmiany"}
                    />
                </div>
            </animated.div>
        </AppModal>
    );
};

export default AiConvertedArticleModal;

